import { useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import notEmpty from '@crehana/utils/notEmpty';

import ADMINDASHBOARD_SEARCH_ORGANIZATION_TEAMS from './graphql/AdminDashboardSearchOrganizationTeams.b2b.graphql';
import type {
  AdminDashboardSearchOrganizationTeams,
  AdminDashboardSearchOrganizationTeamsVariables,
  AdminDashboardSearchOrganizationTeams_organization_teams as TeamType,
} from './graphql/types/AdminDashboardSearchOrganizationTeams';
import type { Response } from './types';

const formatData = ({ teams, slug }: { teams: TeamType[]; slug: string }) =>
  teams?.map(team => ({
    id: team?.originalId || null,
    title: `Ruta ${team?.name}`,
    subTitle: `${team?.totalUsers} usuarios, ${team?.totalCourses} cursos`,
    url: `/org/${slug}/panel/estadisticas/grupales/${team?.originalId}/`,
    type: 'team',
  })) || [];

const useSearchOrganizationTeams = ({
  searchKey,
  slug,
  itemsPerFetch,
}: {
  searchKey: string;
  slug: string;
  itemsPerFetch: number;
}) => {
  const [teamResults, setTeamResults] = useState<Response[]>([]);
  const [response, setResponse] = useState<Response[]>([]);
  const [fetchTeams, { loading: loadingTeams }] = useLazyQuery<
    AdminDashboardSearchOrganizationTeams,
    AdminDashboardSearchOrganizationTeamsVariables
  >(ADMINDASHBOARD_SEARCH_ORGANIZATION_TEAMS, {
    variables: {
      organization: slug,
    },
    context: { clientName: 'b2b' },
    fetchPolicy: 'no-cache',
    onCompleted({ organization }) {
      if (organization?.teams && organization.teams.length > 0) {
        setResponse(
          formatData({
            teams: organization?.teams.filter(notEmpty),
            slug,
          }),
        );
      }
    },
  });

  const filterResults = () => {
    const dataFilter = response.filter(({ title, subTitle }) => {
      const term = searchKey.toLowerCase();

      return (
        title.toLowerCase().includes(term) ||
        (subTitle && subTitle.toLowerCase().includes(term))
      );
    });

    setTeamResults(dataFilter);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    fetchTeams();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (searchKey && response.length > 0) {
      filterResults();
    } else {
      setTeamResults([]);
    }
  }, [searchKey, response]);

  return {
    teamResults: teamResults.slice(0, itemsPerFetch),
    loadingTeams,
    totalOrgCount: teamResults.length,
  };
};

export default useSearchOrganizationTeams;
